/**
 * LiveAnnouncer — WCAG 4.1.3 (Status Messages)
 *
 * Renders a visually hidden ARIA live region. Screen readers announce
 * messages placed here without moving focus.
 *
 * Usage:
 *   import { announce } from './LiveAnnouncer';
 *   announce('Question approved successfully');
 *
 * Mount <LiveAnnouncer /> once in App.js.
 */
import { useEffect, useRef, useCallback } from 'react';

let _announce = () => {};

export function announce(message, politeness = 'polite') {
    _announce(message, politeness);
}

export default function LiveAnnouncer() {
    const politeRef   = useRef(null);
    const assertiveRef = useRef(null);

    useEffect(() => {
        _announce = (message, politeness = 'polite') => {
            const el = politeness === 'assertive' ? assertiveRef.current : politeRef.current;
            if (!el) return;
            // Clear then set forces re-announcement even if same message
            el.textContent = '';
            requestAnimationFrame(() => { el.textContent = message; });
        };
        return () => { _announce = () => {}; };
    }, []);

    return (
        <>
            <div
                ref={politeRef}
                aria-live="polite"
                aria-atomic="true"
                className="live-region"
            />
            <div
                ref={assertiveRef}
                aria-live="assertive"
                aria-atomic="true"
                className="live-region"
            />
        </>
    );
}
